import type { NotificationSlackSettingsMap, NotificationType, SlackChannel, SlackIntegrationPublic } from 'types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';

type SlackSettingsResponse = {
  settings: NotificationSlackSettingsMap;
  slack: SlackIntegrationPublic | null;
};

const QUERY_KEY = ['NOTIFICATION_SLACK_SETTINGS'];
const CHANNELS_KEY = ['SLACK_CHANNELS'];

/**
 * The organisation's Slack connection and what gets posted to it.
 *
 * Owner/Admin only, which the server enforces - these routes 403 for a Member
 * rather than returning an empty state, so the card is not rendered for them.
 */
export const useSlackSettings = (enabled = true) => {
  const client = useClient();
  const queryClient = useQueryClient();

  const query = useQuery<SlackSettingsResponse>({
    queryKey: QUERY_KEY,
    queryFn: () => client.get<SlackSettingsResponse>('/notifications/slack/settings'),
    enabled,
  });

  const isConnected = !!query.data?.slack;

  /**
   * Only fetched once connected, and never cached across a disconnect: the list
   * comes from the workspace, so it is meaningless without one.
   */
  const channelsQuery = useQuery<{ channels: SlackChannel[] }>({
    queryKey: CHANNELS_KEY,
    queryFn: () => client.get<{ channels: SlackChannel[] }>('/notifications/slack/channels'),
    enabled: enabled && isConnected,
    staleTime: 5 * 60 * 1000,
  });

  const updateSettings = useMutation({
    mutationFn: (settings: Partial<Record<NotificationType, boolean>>) =>
      client.put<SlackSettingsResponse>('/notifications/slack/settings', { settings }),
    onSuccess: (data) =>
      queryClient.setQueryData<SlackSettingsResponse>(QUERY_KEY, (previous) =>
        previous ? { ...previous, settings: data.settings } : previous,
      ),
  });

  const connect = useMutation({
    mutationFn: (payload: { code: string; state: string }) =>
      client.post<{ slack: SlackIntegrationPublic }>('/notifications/slack/connect', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      // A reinstall may be a different workspace, so the old channel list is not
      // just stale, it is wrong.
      queryClient.removeQueries({ queryKey: CHANNELS_KEY });
    },
  });

  const selectChannel = useMutation({
    mutationFn: (channel: SlackChannel) =>
      client.put<{ slack: SlackIntegrationPublic }>('/notifications/slack/channel', {
        channelId: channel.id,
        channelName: channel.name,
      }),
    onSuccess: (data) =>
      queryClient.setQueryData<SlackSettingsResponse>(QUERY_KEY, (previous) =>
        previous ? { ...previous, slack: data.slack } : previous,
      ),
  });

  const disconnect = useMutation({
    mutationFn: () => client.delete('/notifications/slack'),
    onSuccess: () => {
      queryClient.setQueryData<SlackSettingsResponse>(QUERY_KEY, (previous) =>
        previous ? { ...previous, slack: null } : previous,
      );
      queryClient.removeQueries({ queryKey: CHANNELS_KEY });
    },
  });

  /** Sends the admin to Slack's consent screen. */
  const startInstall = async () => {
    const { url } = await client.get<{ url: string }>('/notifications/slack/install');
    window.location.href = url;
  };

  return {
    settings: query.data?.settings,
    slack: query.data?.slack ?? null,
    isConnected,
    isLoading: query.isLoading,
    channels: channelsQuery.data?.channels ?? [],
    isLoadingChannels: channelsQuery.isLoading,
    channelsError: channelsQuery.error,
    isSaving: updateSettings.isPending || selectChannel.isPending,
    isConnecting: connect.isPending,
    toggle: (type: NotificationType, value: boolean) => updateSettings.mutate({ [type]: value }),
    connect: connect.mutateAsync,
    selectChannel: selectChannel.mutate,
    disconnect: disconnect.mutate,
    startInstall,
  };
};
